import { Cpu, BookOpenCheck, ShieldCheck } from "lucide-react";
import { Reveal } from "./Reveal";

export default function QuietTechnology() {
  return (
    <section
      id="technology"
      data-testid="quiet-tech-section"
      className="relative py-20 lg:py-32 bg-brand-navy text-white overflow-hidden"
    >
      <img
        src="https://images.pexels.com/photos/27243769/pexels-photo-27243769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt=""
        aria-hidden="true"
        width="940"
        height="650"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.14]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/60 via-transparent to-brand-navy/80" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-20">
          <Reveal className="col-span-12 lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-pale font-semibold mb-4">
              Below deck
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-2xl sm:text-3xl lg:text-4xl">
              Built on tools you already trust.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 flex items-end">
            <p className="text-base text-slate-300 leading-relaxed">
              You'll never be asked to learn anything new. Everything runs
              quietly inside the systems your business already uses — and
              shows up only as work that's already done.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <Reveal>
            <div
              data-testid="copilot-crm-card"
              className="rounded-3xl bg-white/[0.06] backdrop-blur-md border border-white/10 p-8 sm:p-10 h-full transition-colors duration-300 hover:border-brand-teal/40"
            >
              <span className="w-12 h-12 rounded-2xl bg-brand-teal/15 text-brand-pale flex items-center justify-center mb-6">
                <Cpu size={22} />
              </span>
              <h3 className="font-display font-semibold text-xl sm:text-2xl mb-3">
                Sales &amp; projects, connected
              </h3>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                When a deal is won, the work begins without a handoff meeting.
                Status updates write themselves, and anyone can see what's
                sold, what's done, and what's left.
              </p>
              <span
                data-testid="mcp-tools-badge"
                className="inline-flex items-center gap-2 rounded-full bg-brand-teal/10 border border-brand-teal/25 text-brand-pale font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2"
              >
                Won deal → project → done
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="copilot-books-card"
              className="rounded-3xl bg-white/[0.06] backdrop-blur-md border border-white/10 p-8 sm:p-10 h-full transition-colors duration-300 hover:border-brand-teal/40"
            >
              <span className="w-12 h-12 rounded-2xl bg-brand-teal/15 text-brand-pale flex items-center justify-center mb-6">
                <BookOpenCheck size={22} />
              </span>
              <h3 className="font-display font-semibold text-xl sm:text-2xl mb-3">
                The numbers, always current
              </h3>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                Delivered work becomes invoices, payments get matched, and your
                cash position is always up to date — ask a plain question, get
                a plain answer.
              </p>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-teal/10 border border-brand-teal/25 text-brand-pale font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2">
                Invoices · payments · cash position
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex items-start gap-3 text-sm text-slate-400 max-w-2xl">
            <ShieldCheck size={16} className="text-brand-pale mt-0.5 shrink-0" />
            <p className="leading-relaxed">
              Works with the tools your business already runs on. Your data
              stays in your systems, under your control — every change waits
              for your yes, and is recorded so you can always see who did what.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
